import { Divider, FormControlLabel, Grid, Switch } from '@mui/material';
import { GridColumn } from '@progress/kendo-react-grid';
import { Form, Formik, FormikProps } from 'formik';
import { forwardRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import {
  ActionButton,
  PrimaryButton,
  TertiaryButton
} from 'src/components/shared/Button/Button';
import { Checkbox } from 'src/components/shared/Checkbox/Checkbox';
import { DataGrid } from 'src/components/shared/DataGrid/DataGrid';
import { Autocomplete } from 'src/components/shared/Forms/Autocomplete/Autocomplete';
import { FormField } from 'src/components/shared/Forms/FormHelpers';
import { InputField } from 'src/components/shared/Forms/Input/InputField';
import { ModalFooter } from 'src/components/shared/Modal/Modal';
import { BodyType } from 'src/components/shared/Typography/BodyType';
import { GradeDto } from 'src/store/grades/grade';
import { selectActiveGrades } from 'src/store/grades/grades.selectors';
import { selectActiveProductTypes } from 'src/store/producttypes/producttypes.selectors';
import { SegmentDetailDto, SegmentGradeDto } from 'src/store/segments/segment';
import {
  maxLength,
  required,
  runValidations,
  validationRunner
} from 'src/utils/validation';

type Props = {
  segment: Nullable<SegmentDetailDto>;
  onCancel: () => void;
  onSubmit: (segment: SegmentDetailDto) => void;
};

export const SegmentForm = forwardRef<FormikProps<SegmentDetailDto>, Props>(
  (props, ref) => {
    const { onSubmit, onCancel } = props;

    const [showValidationErrors, setShowValidationErrors] =
      useState<boolean>(false);
    const [segment, setSegment] = useState<SegmentDetailDto>(
      props.segment || ({ markLabel: false, active: true } as SegmentDetailDto)
    );

    const [grades, setGrades] = useState<GradeDto[]>([]);

    const { t } = useTranslation();

    const productTypes = useSelector(selectActiveProductTypes);
    const grades$ = useSelector(selectActiveGrades);

    const validate = (segment: SegmentDetailDto) => {
      const validationRunners = [
        validationRunner('no', t('props.number'), required),
        validationRunner('name', t('props.name'), required, maxLength(50)),
        validationRunner('description', t('props.description'), maxLength(200)),
        validationRunner('productTypeId', t('productType.singular'), required)
      ];
      return runValidations(segment, validationRunners);
    };

    useEffect(() => {
      setGrades(
        grades$.filter(
          grade => !segment.segmentGrades?.some(sg => sg.gradeId === grade.id)
        )
      );
    }, [segment.segmentGrades, grades$]);

    const onAddGrade = (
      segment: SegmentDetailDto,
      grade: GradeDto,
      setFieldValue: any
    ) => {
      const segmentGrades = [
        ...(segment.segmentGrades ?? []),
        {
          segmentId: segment.id,
          segmentNo: +segment.no,
          segmentName: segment.name,
          gradeId: grade.id,
          gradeNo: +grade.no,
          gradeName: grade.name
        } as SegmentGradeDto
      ];
      setSegment({ ...segment, segmentGrades: segmentGrades });
      setFieldValue('segmentGrades', segmentGrades);
    };

    const onRemoveGrade = (
      segment: SegmentDetailDto,
      segmentGrade: SegmentGradeDto,
      setFieldValue: any
    ) => {
      const segmentGrades = segment.segmentGrades.filter(
        sg => sg.gradeId !== segmentGrade.gradeId
      );
      setSegment({ ...segment, segmentGrades: segmentGrades });
      setFieldValue('segmentGrades', segmentGrades);
    };

    return (
      <Formik
        initialValues={segment}
        validate={validate}
        innerRef={ref}
        onSubmit={(values, { setSubmitting }) => {
          onSubmit(values);
          setSubmitting(false);
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldValue,
          isSubmitting,
          submitCount
        }) => (
          <Form onSubmit={handleSubmit}>
            <Grid container>
              <Grid item xs={3}>
                <InputField
                  name="no"
                  type="number"
                  label={t('props.number')}
                  value={values.no}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.no}
                  displayError={touched.no || submitCount > 0}
                />
              </Grid>
              <Grid item xs={9}>
                <InputField
                  name="name"
                  label={t('props.name')}
                  value={values.name}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  errorMessage={errors.name}
                  displayError={touched.name || submitCount > 0}
                />
              </Grid>
            </Grid>
            <InputField
              name="description"
              label={t('props.description')}
              value={values.description}
              onChange={handleChange}
              onBlur={handleBlur}
              errorMessage={errors.description}
              displayError={touched.description || submitCount > 0}
            />
            <div className="mb-24">
              <FormControlLabel
                control={
                  <Switch
                    checked={values.active}
                    onChange={handleChange}
                    name="active"
                  />
                }
                label={t('props.active')}
              />
              <Checkbox
                label={t('segment.markLabel')}
                checked={values.markLabel}
                name="markLabel"
                onChange={handleChange}
              />
            </div>
            <FormField>
              <Autocomplete
                options={productTypes}
                label={t('productType.singular')}
                optionField="name"
                value={values.productType}
                onChange={productType =>
                  setFieldValue('productTypeId', productType?.id)
                }
                onBlur={handleBlur}
                errorMessage={errors.productTypeId}
                displayError={!!errors.productTypeId && showValidationErrors}
              />
            </FormField>

            <Divider className="mb-32" />

            <Grid container>
              <Grid item xs={6}>
                <DataGrid
                  items={values.segmentGrades}
                  style={{ height: 400 }}
                  initialSort={[{ field: 'gradeName', dir: 'asc' }]}
                >
                  <GridColumn
                    field="gradeName"
                    title={t('grade.plural')}
                    cell={props => (
                      <td>
                        <div className="flex jc-sb align-center">
                          <BodyType truncate style={{ maxWidth: '80%' }}>
                            {props.dataItem.gradeName}
                          </BodyType>
                          <ActionButton
                            icon="la la-times"
                            onClick={() =>
                              onRemoveGrade(
                                values,
                                props.dataItem,
                                setFieldValue
                              )
                            }
                          />
                        </div>
                      </td>
                    )}
                  />
                </DataGrid>
              </Grid>

              <Grid item xs={6}>
                <DataGrid
                  items={grades}
                  style={{ height: 400 }}
                  initialSort={[{ field: 'name', dir: 'asc' }]}
                >
                  <GridColumn
                    field="name"
                    title={t('grade.all')}
                    cell={props => (
                      <td>
                        <div className="flex jc-sb align-center">
                          <BodyType truncate style={{ maxWidth: '80%' }}>
                            {props.dataItem.name}
                          </BodyType>
                          <ActionButton
                            icon="la la-plus"
                            onClick={() =>
                              onAddGrade(values, props.dataItem, setFieldValue)
                            }
                          />
                        </div>
                      </td>
                    )}
                  />
                </DataGrid>
              </Grid>
            </Grid>

            <ModalFooter>
              <TertiaryButton
                onClick={onCancel}
                label={t('cancel')}
                className="mr-32"
              />
              <PrimaryButton
                type="submit"
                label={t('save.verb')}
                disabled={isSubmitting}
                onClick={() => {
                  setShowValidationErrors(true);
                }}
              />
            </ModalFooter>
          </Form>
        )}
      </Formik>
    );
  }
);
